'use client';

import { useChatContext } from '@/contexts/ChatContext';
import Messages from './Messages';
import ChatInput from './ChatInput';
import { X, Monitor, FileCode2 } from 'lucide-react';
import { MockWorkflowSidePanel } from './MockWorkflowMessage';

export default function ChatArea() {
  const {
    messages,
    sidePanelOpen,
    sidePanelTab,
    previewUrl,
    openSidePanel,
    closeSidePanel,
  } = useChatContext();
  const isEmpty = messages.length === 0;
  const lastWorkflowMessage = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant' && m.metadata?.workflow);
  const lastWorkflow = (lastWorkflowMessage?.metadata?.workflow as any) ?? null;
  const hasHtmlFile = Boolean(
    lastWorkflow?.ide?.files?.some((f: any) => typeof f?.path === 'string' && f.path.toLowerCase().endsWith('.html')),
  );
  const isPreviewReady = Boolean(lastWorkflow?.preview?.html) || hasHtmlFile || Boolean(previewUrl);

  return (
    <main className="flex-1 min-w-0 flex h-full theme-bg">
      <div className="flex-1 min-w-0 flex flex-col h-full">
        <div className="flex-1 min-h-0 relative">
          <Messages />
        </div>

        {!isEmpty && (
          <div className="px-6 py-4 theme-bg theme-border border-t">
            <ChatInput />

            <p className="text-center text-xs theme-text-muted mt-2">
              O Paladino pode cometer erros. Por isso, é bom checar as respostas.
            </p>
          </div>
        )}
      </div>

      {sidePanelOpen && (
        <aside className="w-[920px] max-w-[75vw] min-w-[560px] h-full border-l theme-border bg-white dark:bg-[#121216] flex flex-col">
          <div className="px-4 py-3 border-b theme-border flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openSidePanel('ide')}
                className={[
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  sidePanelTab === 'ide'
                    ? 'bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]'
                    : 'theme-card theme-border theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]',
                ].join(' ')}
              >
                <FileCode2 className="w-3.5 h-3.5" />
                IDE
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!isPreviewReady) return;
                  openSidePanel('preview');
                }}
                disabled={!isPreviewReady}
                className={[
                  'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  sidePanelTab === 'preview'
                    ? 'bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]'
                    : 'theme-card theme-border theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)]',
                ].join(' ')}
              >
                <Monitor className="w-3.5 h-3.5" />
                Preview
              </button>
            </div>

            <button
              type="button"
              onClick={closeSidePanel}
              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border theme-border theme-card hover:bg-[color:color-mix(in_oklab,var(--card-bg)_92%,transparent)] transition-colors"
              aria-label="Fechar painel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {lastWorkflow ? (
              <MockWorkflowSidePanel
                messageId={lastWorkflowMessage!.id}
                workflow={lastWorkflow}
                tab={sidePanelTab}
              />
            ) : sidePanelTab === 'preview' && previewUrl ? (
              <iframe title="Preview" src={previewUrl} className="w-full h-full" />
            ) : sidePanelTab === 'ide' ? (
              <div className="h-full p-4 text-sm theme-text-muted">
                Nenhuma IDE disponível para este chat.
              </div>
            ) : (
              <div className="h-full p-4 text-sm theme-text-muted">
                Nenhum preview disponível ainda.
              </div>
            )}
          </div>
        </aside>
      )}
    </main>
  );
}
